import { Dispatch, useCallback, useMemo, useReducer, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "@/store";
import { saveWeightValues } from "@/store/slice/datasetSlice";
import { setSpatialConfig } from "@/store/slice/spatialSlice";
import { DatasetsMap, WeightValueEntry } from "@/types/dataset";
import { ScoringOptions, ScoringOptionsEnum } from "@/types/spatial";
import { areObjectsDifferentDeeply } from "@/utils/common";
import { getAvailableDatasets } from "@/utils/dataset";
import {
  getIsIncludedScoring,
  hasInvalidAdvancedNormalization,
  hasNonZeroValue
} from "@/utils/weightValues";

import { useDeepCompareMemo } from "use-deep-compare";

import {
  calculateAttributeGlobalPercentages,
  calculateDatasetWeightPercentages,
  getCombinedWeightValues
} from "./utils";
import {
  createInitialWeightSettingsState,
  WeightSettingsAction,
  WeightSettingsMode,
  weightSettingsReducer,
  WeightSettingsState
} from "./weightSettingsReducer";

/**
 * The only ways consumers may change anything. `dispatch` feeds semantic
 * actions straight into the reducer (the reducer owns all update logic);
 * the named handlers wrap save/reset/close flows.
 */
interface DataWeightSettingsActions {
  dispatch: Dispatch<WeightSettingsAction>;
  setSelectedScoringOption: (scoringOption: ScoringOptions | null) => void;
  setMode: (mode: WeightSettingsMode) => void;
  setIntercept: (intercept: string) => void;
  resetAllWeights: () => void;
  /** Persists the settings and returns `true`. In advanced mode with invalid
   * Mean/Stdv inputs it instead reveals the errors and returns `false` (no
   * save) so the caller can keep the panel open. */
  handleSaveSetting: () => boolean;
}

/**
 * Read-only values COMPUTED from `state` + the Redux store on every hook
 * render — nothing here is stored or settable. If a value can be calculated
 * from existing sources it belongs here, not in `state` (keeping it in state
 * would require syncing it manually and it could drift out of date).
 */
interface DataWeightSettingsDerived {
  /** Datasets from the Redux store filtered to the ones usable for weighting. */
  availableDatasets: DatasetsMap;
  /** Dataset ids in the order the panels should render them. */
  visualizationOrder: string[];
  /** Ids of datasets added after the saved baseline — the "new" datasets whose
   * addition auto-adjusted the weights. Advanced panel highlights their rows. */
  weightAdjustedDatasetIds: string[];
  /** Per-dataset share of the total weight, formatted ("33.33"). */
  datasetWeightPercentages: Record<string, string>;
  /** Per-dataset → per-attribute global percentage, formatted. */
  allAttributeGlobalPercentages: Record<string, Record<string, string>>;
  /** Number of attribute controls rendered across available datasets. */
  totalAttributes: number;
  /** True when local edits differ from the saved Redux values (enables Reset). */
  hasWeightValuesChanged: boolean;
  /** True when every included attribute weight is zero (blocks Save). */
  isAllAttributeWeightZero: boolean;
  /** True when every included dataset weight is zero (blocks Save). */
  isAllDatasetWeightZero: boolean;
  /** True in advanced mode when any included attribute's Mean/Stdv is invalid
   * (empty, non-number, or — for Stdv — `<= 0`). Blocks Save. */
  isAdvancedNormalizationInvalid: boolean;
}

/**
 * Hook return shape, grouped by role so consumers can destructure exactly the
 * kind of dependency they have:
 * - `state`    — raw values owned by the reducer (source of truth for edits)
 * - `actions`  — the only way to mutate `state` or trigger save/reset/close
 * - `derived`  — read-only computations over `state` + Redux (see above)
 */
export interface DataWeightSettingsValue {
  state: WeightSettingsState;
  actions: DataWeightSettingsActions;
  derived: DataWeightSettingsDerived;
}

/**
 * Owns all Data Weight settings state (via reducer), derived values, and
 * handlers. Called once at the popup root; the resulting value is passed down
 * to the panels and sections as props (no context). Only this hook knows that
 * the source of truth is Redux.
 */
export const useDataWeightSettings = (): DataWeightSettingsValue => {
  const reduxDispatch = useDispatch();
  const {
    datasets,
    weightValues,
    visualizationOrder,
    weightAdjustedDatasetIds
  } = useSelector((rootState: RootState) => rootState.dataset);
  const { spatialConfig } = useSelector(
    (rootState: RootState) => rootState.spatial
  );

  const [state, dispatch] = useReducer(
    weightSettingsReducer,
    {
      weightValues,
      scoringOption: spatialConfig.scoringOption,
      intercept: spatialConfig.intercept
    },
    createInitialWeightSettingsState
  );
  const { localWeightValues } = state;

  // The initial state already cloned `weightValues`
  // (createInitialWeightSettingsState), so skip the mount run and only
  // re-sync when the store value actually changes afterwards.
  const lastSyncedWeightValues = useRef(weightValues);

  if (lastSyncedWeightValues.current !== weightValues) {
    lastSyncedWeightValues.current = weightValues;
    dispatch({ type: "weightValues/syncFromStore", weightValues });
  }

  // Project configuration can hydrate after this popup has mounted. Keep the
  // edit model aligned with the persisted advanced-mode signal and intercept,
  // just as the weight values above are kept aligned with their store source.
  const lastSyncedSpatialConfig = useRef({
    scoringOption: spatialConfig.scoringOption,
    intercept: spatialConfig.intercept
  });
  if (
    lastSyncedSpatialConfig.current.scoringOption !==
      spatialConfig.scoringOption ||
    lastSyncedSpatialConfig.current.intercept !== spatialConfig.intercept
  ) {
    lastSyncedSpatialConfig.current = {
      scoringOption: spatialConfig.scoringOption,
      intercept: spatialConfig.intercept
    };
    dispatch({
      type: "spatialConfig/syncFromStore",
      scoringOption: spatialConfig.scoringOption,
      intercept: spatialConfig.intercept
    });
  }

  const availableDatasets = useMemo(
    () => getAvailableDatasets(datasets),
    [datasets]
  );

  const totalAttributes = useMemo(
    () =>
      Object.entries(availableDatasets).reduce(
        (sum, [datasetId, dataset]) =>
          sum +
          (dataset.properties.spatialType === "poi"
            ? 1
            : Object.keys(weightValues[datasetId]?.attributes ?? {}).length),
        0
      ),
    [availableDatasets, weightValues]
  );

  const datasetWeightPercentages = useMemo(
    () => calculateDatasetWeightPercentages(localWeightValues),
    [localWeightValues]
  );

  const allAttributeGlobalPercentages = useDeepCompareMemo(
    () =>
      Object.fromEntries(
        visualizationOrder.map((datasetId) => [
          datasetId,
          calculateAttributeGlobalPercentages(
            Object.fromEntries(
              Object.entries(
                localWeightValues[datasetId]?.attributes || {}
              ).filter(([, value]) => value !== undefined)
            ) as Record<string, WeightValueEntry>,
            datasetWeightPercentages[datasetId]
          )
        ])
      ),
    [localWeightValues, datasetWeightPercentages, visualizationOrder]
  );

  const hasWeightValuesChanged = useDeepCompareMemo(
    () =>
      areObjectsDifferentDeeply(localWeightValues, weightValues) &&
      Object.keys(localWeightValues).length ===
        Object.keys(weightValues).length,
    [localWeightValues, weightValues]
  );

  const isAllAttributeWeightZero = useDeepCompareMemo(
    () =>
      !visualizationOrder.some((datasetId) => {
        if (localWeightValues[datasetId]?.include_scoring === false) {
          return false;
        }
        return Object.values(localWeightValues[datasetId]?.attributes || {})
          .filter(getIsIncludedScoring)
          .some(hasNonZeroValue);
      }),
    [localWeightValues, visualizationOrder]
  );

  const isAllDatasetWeightZero = useDeepCompareMemo(
    () =>
      !visualizationOrder.some((datasetId) => {
        if (localWeightValues[datasetId]?.include_scoring === false) {
          return false;
        }
        return hasNonZeroValue(localWeightValues[datasetId]?.weight);
      }),
    [localWeightValues, visualizationOrder]
  );

  // Only meaningful in advanced mode, where every included attribute requires a
  // valid Mean/Stdv pair (the wire emits LOG_Z_SCORE regardless).
  const isAdvancedNormalizationInvalid = useDeepCompareMemo(
    () =>
      state.mode === "advanced" &&
      hasInvalidAdvancedNormalization(localWeightValues, datasets),
    [state.mode, localWeightValues, datasets]
  );

  const setSelectedScoringOption = useCallback(
    (scoringOption: ScoringOptions | null) => {
      dispatch({ type: "scoringOption/set", scoringOption });
    },
    []
  );

  const setMode = useCallback((mode: WeightSettingsMode) => {
    dispatch({ type: "mode/set", mode });
  }, []);

  const setIntercept = useCallback((intercept: string) => {
    dispatch({ type: "intercept/set", intercept });
  }, []);

  const resetAllWeights = useCallback(() => {
    dispatch({ type: "weightValues/syncFromStore", weightValues });
  }, [weightValues]);

  const handleSaveSetting = useCallback((): boolean => {
    // Advanced mode requires a valid Mean/Stdv on every included attribute. On an
    // invalid attempt, reveal the red borders (mark the attempt) and block the
    // save so the panel stays open.
    if (state.mode === "advanced" && isAdvancedNormalizationInvalid) {
      dispatch({ type: "advanced/markValidationAttempted" });
      return false;
    }

    const combinedWeightValues = getCombinedWeightValues(
      localWeightValues,
      allAttributeGlobalPercentages
    );
    reduxDispatch(saveWeightValues({ weightValues: combinedWeightValues }));
    // Advanced mode persists LOG_Z_SCORE directly — that signal (not z_score
    // inspection) is what drives advanced normalization downstream.
    const scoringOption =
      state.mode === "advanced"
        ? ScoringOptionsEnum.LOG_Z_SCORE
        : state.selectedScoringOption;
    reduxDispatch(
      setSpatialConfig({
        ...spatialConfig,
        scoringOption,
        intercept: state.intercept
      })
    );
    return true;
  }, [
    localWeightValues,
    allAttributeGlobalPercentages,
    reduxDispatch,
    spatialConfig,
    state.mode,
    state.selectedScoringOption,
    state.intercept,
    isAdvancedNormalizationInvalid,
    dispatch
  ]);

  return useMemo<DataWeightSettingsValue>(
    () => ({
      state,
      actions: {
        dispatch,
        setSelectedScoringOption,
        setMode,
        setIntercept,
        resetAllWeights,
        handleSaveSetting
      },
      derived: {
        availableDatasets,
        visualizationOrder,
        weightAdjustedDatasetIds,
        datasetWeightPercentages,
        allAttributeGlobalPercentages,
        totalAttributes,
        hasWeightValuesChanged,
        isAllAttributeWeightZero,
        isAllDatasetWeightZero,
        isAdvancedNormalizationInvalid
      }
    }),
    [
      state,
      setSelectedScoringOption,
      setMode,
      setIntercept,
      resetAllWeights,
      handleSaveSetting,
      availableDatasets,
      visualizationOrder,
      weightAdjustedDatasetIds,
      datasetWeightPercentages,
      allAttributeGlobalPercentages,
      totalAttributes,
      hasWeightValuesChanged,
      isAllAttributeWeightZero,
      isAllDatasetWeightZero,
      isAdvancedNormalizationInvalid
    ]
  );
};
